import { Injectable } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, throwError, Subscription } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom, distinctUntilChanged } from 'rxjs/operators';

import { AvailableRuleItem, AvailableRuleEventCondition, RuleEvent, RuleListItem } from '@app/models/rules';

import * as fromRulesModel from '@app/models/rules';

import { SettingsRulesRequests } from './rules.requests';

import * as fromStore from './rules.state';
import * as fromActions from './rules.actions';

@Injectable()
export class SettingsRulesEffects {

    constructor(private actions$: Actions, private store$: Store<fromStore.SettingsRules>, private requests: SettingsRulesRequests) {}

    /**
     * Persisted rules effects
     */

    public loadRules$ : Observable<fromActions.SettingsRulesActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadRules>(fromActions.SettingsRulesActionType.LoadRules),
        mergeMap((action) => {
            return this.requests.loadRules$().pipe(
                map((rules: fromRulesModel.RuleListItem[]) => {
                    return new fromActions.LoadRulesSuccess(rules);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadRulesError());
                })
            );
        })
    ));

    public loadRule$ : Observable<fromActions.SettingsRulesActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadRule>(fromActions.SettingsRulesActionType.LoadRule),
        mergeMap((action) => {
            return this.requests.loadRule$(action.ruleId).pipe(
                map((rule: RuleEvent) => {
                    return new fromActions.LoadRuleSuccess(rule);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadRuleError());
                })
            );
        })
    ));

    public loadRuleSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadRuleSuccess>(fromActions.SettingsRulesActionType.LoadRuleSuccess),
        switchMap((action) => {
            return  [ new fromActions.SetRuleEvent(action.rule.key) ];
            })
    ));

    public setLoadedRuleEvent$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetRuleEvent>(fromActions.SettingsRulesActionType.SetRuleEvent),
        switchMap((action) => {
            return  [ new fromActions.RuleEventChangedSuccess(action.eventKey) ];
            })
    ));

    // when rule editor event selector is chagned after first option was set.
    public updateRuleEvent$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateRuleEvent>(fromActions.SettingsRulesActionType.UpdateRuleEvent),
        switchMap((action) => [
            new fromActions.RuleEventChangedSuccess(action.eventKey)
        ])
    ));

    /**
     * Auxiliar and edition rules effects
     */
    
    public loadAvailableEvents$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadAvailableEvents>(fromActions.SettingsRulesActionType.LoadAvailableEvents),
        switchMap(() => {
            return this.requests.loadAvailableEvents$().pipe(
                map((events: fromRulesModel.AvailableRuleEventElement[]) => {
                    return new fromActions.LoadAvailableEventsSuccess(events);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadAvailableEventsError());
                })
            );
        })
    ));

    /** 
     * When an event key is selected on the editor EVENT/ACTION selector, this effect is triggered.
     */
    public ruleEventChangedSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.RuleEventChangedSuccess>(fromActions.SettingsRulesActionType.RuleEventChangedSuccess),
        withLatestFrom(this.store$.select(fromStore.settingsRulesAvailableEventsSelector)),
        mergeMap(([action, availableEvents]) => {

            // get the event
            const currEventIdx = availableEvents.findIndex((ae => ae.key == action.eventKey));
            if(currEventIdx == -1){
                return [];
            }
            // check pending available conditions and actions that not already loaded 
            return [
                new fromActions.CheckAvailableConditions(availableEvents[currEventIdx].conditions),
                new fromActions.CheckAvailableActions(availableEvents[currEventIdx].actions)
            ]
        })
    ));


    public checkAvailableConditions$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.CheckAvailableConditions>(fromActions.SettingsRulesActionType.CheckAvailableConditions),
        withLatestFrom(this.store$.select(fromStore.settingsRulesAvailableEventsConditionsSelector)),
        switchMap(([action,conditionsAvailable]) => {
            const sreCEvents = [] 
            // check if conditions are load
            action.conditions.forEach(cond => {
                if(!conditionsAvailable.some((citem) => citem.key == cond.key))
                    sreCEvents.push(new fromActions.LoadAvailableConditions(cond.key));
            });
            // TODO oct-2023: this action will trigger a call succes for the load first action and condition  variables for the selected event key.
            return sreCEvents;
        })
    ));

    public checkAvailableActions$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.CheckAvailableActions>(fromActions.SettingsRulesActionType.CheckAvailableActions),
        withLatestFrom(this.store$.select(fromStore.settingsRulesAvailableEventsActionsSelector)),
        switchMap(([action,actionsAvailable]) => {
            const sreaEvents = [] 
            // check if actions are load already
            action.actions.forEach(act => {
                if(!actionsAvailable.some((aitem) => aitem.key == act.key)){
                    sreaEvents.push(new fromActions.LoadAvailableActions(act));
                } 
            });
            // IF not available actions to load, we add the first action directly. Is a new event in new rule
            if( sreaEvents.length == 0){
                sreaEvents.push(new fromActions.AddRuleAction());
            }
            // this action will trigger a call succes for the load first action and condition  variables for the selected event key.
            return sreaEvents;
        })
    ));

    public loadAvailableConditions$ : Observable<fromActions.SettingsRulesActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadAvailableConditions>(fromActions.SettingsRulesActionType.LoadAvailableConditions),
        mergeMap((action) => {
            return this.requests.loadAvailableConditions$(action.eventKey).pipe(
                map((conditions: fromRulesModel.AvailableRuleEventCondition[]) => {
                    return new fromActions.LoadAvailableConditionsSuccess(action.eventKey, conditions);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadAvailableConditionsError());
                })
            );
        })
    ));

    public loadAvailableActions$ : Observable<fromActions.SettingsRulesActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadAvailableActions>(fromActions.SettingsRulesActionType.LoadAvailableActions),
        mergeMap((action) => {

            return this.requests.loadAvailableActions$(action.actionItem.key).pipe(
                map((actionVariables: fromRulesModel.AvailableRuleActionVariable[]) => {
                    return new fromActions.LoadAvailableActionsSuccess(action.actionItem, actionVariables);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadAvailableActionsError());
                })
            );
        })
    ));

    public changeRuleCondition$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.ChangeRuleCondition>(fromActions.SettingsRulesActionType.ChangeRuleCondition),
        withLatestFrom(this.store$.select(fromStore.settingsRulesEditCurrentSelector)),
        switchMap(([action, editing]) => {
            let currentRuleCondition = editing.conditions.filter((rc) => rc.form_id == action.condition.form_id);
            if(currentRuleCondition?.length > 0){
                return [new fromActions.ChangeRuleConditionValues(action.condition)]
            }

            return []
          
        })
    ));

    /**
     * actions in edit screen update
     */

    /**
     * @deprecated since may-2024 
     * ChangeRuleAction ChangeRuleActionKey will be separated, so changes in action content, 
     * and key value do not require the same management of the data in the current rule
     * Since this update, a rule should be able to have in the actions the same key more than once.
     */
    // public changeRuleAction$ = createEffect(() => this.actions$.pipe(
    //     ofType<fromActions.ChangeRuleAction>(fromActions.SettingsRulesActionType.ChangeRuleAction),
    //     withLatestFrom(this.store$.select(fromStore.settingsRulesEditCurrentSelector)),
    //     switchMap(([action, editing]) => {
    //         const currAction = editing.actions.find((ca => ca.form_id == action.action.form_id));
    //         if(action.action.key != currAction.key)
    //             return [new fromActions.ChangeRuleActionKey(action.action)]

    //         return []
          
    //     })
    // ));

    public setActiveRule$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetActiveRule>(fromActions.SettingsRulesActionType.SetActiveRule),
        mergeMap((action) => {
            return this.requests.setActiveRule$(action.ruleId, action.active).pipe(
                map((rule: any) => {
                    return new fromActions.SetActiveRuleSuccess();
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetActiveRuleError(action.ruleId, action.active));
                })
            );
        })
    ));

    public saveRule$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SaveRule>(fromActions.SettingsRulesActionType.SaveRule),
        withLatestFrom(this.store$.select(fromStore.settingsRulesEditCurrentSelector)),
        mergeMap(([action, editing]) => {
            if(editing.id > 0)
                return of(new fromActions.UpdateRule(editing.id, editing))
            else
                return of(new fromActions.CreateRule(editing))
          
        })
    ));


    public updateRule$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateRule>(fromActions.SettingsRulesActionType.UpdateRule),
        mergeMap((action) => {
            return this.requests.updateRule$(action.id, action.rule).pipe(
                map((rule: RuleEvent) => {
                    return new fromActions.SaveRuleSuccess(rule);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SaveRuleError());
                })
            );
        })
    ));

    public createRule$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.CreateRule>(fromActions.SettingsRulesActionType.CreateRule),
        mergeMap((action) => {
            return this.requests.createRule$(action.rule).pipe(
                map((rule: RuleEvent) => {
                    return new fromActions.SaveRuleSuccess(rule);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SaveRuleError());
                })
            );
        })
    ));
}
